import { Component, inject } from '@angular/core';
import {
  CaptureLocation,
  CaptureLocationUnavailableError,
  CaptureLocationService,
} from '../../shared/services/capture-location.service';
import { RouteService } from '../../shared/services/route.service';
import { InputGeneral } from '../../shared/inputs/input-general/input-general';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom, forkJoin } from 'rxjs';
import { VisitasService } from '../../core/services/visitas.service';
import { VpnService } from '../../core/services/vpn.service';
import { GeneralSnackService } from '../../shared/services/general-snack.service';
import { LoadGeneralService } from '../../shared/services/load-general.service';
import { AdoutUserFormsService } from '../services/forms-catalog.service';
import { TicketClosingFieldsStorageService } from '../../core/services/ticket-closing-fields-storage.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [InputGeneral, ReactiveFormsModule, MatCheckbox, MatButtonModule, TranslatePipe],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css',
})
export class CreditoHomeComponent {
  private readonly captureLocationService = inject(CaptureLocationService);
  private readonly vpnService = inject(VpnService);
  private readonly generalSnackService = inject(GeneralSnackService);
  private readonly adoutUserFormsService = inject(AdoutUserFormsService);
  private readonly closingFieldsStorageService = inject(TicketClosingFieldsStorageService);

  public homeForm: FormGroup;
  public isSubmitting = false;
  public NAME_PATTERN = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]{3,100}$/;
  public CURP_PATTERN =
    /^[A-Z][AEIOUX][A-Z]{2}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[HMX](AS|BC|BS|CC|CL|CM|CS|CH|DF|DG|GT|GR|HG|JC|MC|MN|MS|NT|NL|OC|PL|QT|QR|SP|SL|SR|TC|TS|TL|VZ|YN|ZS|NE)[B-DF-HJ-NP-TV-Z]{3}[A-Z0-9]\d$/i;
  constructor(
    private servicesGeneral: RouteService,
    public visitasService: VisitasService,
    private loadGeneralService: LoadGeneralService,
  ) {
    this.homeForm = new FormGroup({
      username: new FormControl('', {
        validators: [
          Validators.required,
          Validators.minLength(3),
          Validators.pattern(this.NAME_PATTERN),
        ],
      }),
      curp: new FormControl('', {
        validators: [Validators.required, Validators.pattern(this.CURP_PATTERN)],
      }),
      isAtHome: new FormControl(false, {
        validators: [Validators.requiredTrue],
      }),
      acceptTerms: new FormControl(false, {
        validators: [Validators.requiredTrue],
      }),
    });
    this.homeForm.get('curp')?.valueChanges.subscribe((value) => {
      this.homeForm.get('curp')?.setValue((value ?? '').toUpperCase(), { emitEvent: false });
    });
  }

  private async newVisitas(): Promise<void> {
    const curp = this.homeForm.get('curp')?.value ?? '';
    const nombreCompleto = this.homeForm.get('username')?.value ?? '';

    let clientIp = '';

    clientIp = (await firstValueFrom(this.vpnService.getDiagnosticoIp())) ?? '';

    const [visitaResponse, vpnResponse] = await firstValueFrom(
      forkJoin([
        this.visitasService.newVisitas({
          nombreCompleto,
          curp,
          avisoPrivacidadAceptado: this.homeForm.get('acceptTerms')?.value ?? false,
        }),
        this.vpnService.getVpn(clientIp),
      ]),
    );

    if (!visitaResponse) {
      await this.generalSnackService.viewSnacAlert(
        'No fue posible registrar la visita inicial.',
        'error',
        'Error al registrar visita',
      );
      return;
    }

    const isVpnDetected = Boolean(vpnResponse?.esVpn);
    this.closingFieldsStorageService.setValue(
      this.adoutUserFormsService.formKeys.locations.vpn,
      isVpnDetected ? 'Si' : 'No',
    );

    localStorage.setItem(
      'visitaResponse',
      JSON.stringify({
        ...visitaResponse,
        curp,
        nombreCompleto,
      }),
    );
    this.goToUrl(['credito/user-info'], {});
  }

  public goToUrl(route: string[], queryParams: { [key: string]: string | number }): void {
    this.servicesGeneral.goToUrl(route, queryParams);
  }

  public async verifyForm(): Promise<void> {
    if (this.homeForm.invalid || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.loadGeneralService.emitLoad(true, true);
    const curp = `${this.homeForm.get('curp')?.value ?? ''}`.trim().toUpperCase();

    try {
      if (!curp) {
        await this.generalSnackService.viewSnacAlert(
          'El CURP ingresado no es válido.',
          'alert',
          'CURP inválido',
        );
        return;
      }

      await this.captureLocationService.getCaptureLocation();
      await this.newVisitas();
    } catch (error) {
      if (!(error instanceof CaptureLocationUnavailableError)) {
        await this.generalSnackService.viewSnacAlert(
          error instanceof Error && error.message
            ? error.message
            : 'No fue posible continuar con la solicitud. Intenta nuevamente.',
          'error',
          'Error al procesar la solicitud',
        );
      }
    } finally {
      this.isSubmitting = false;
      this.loadGeneralService.emitLoad(false);
    }
  }
}
